/**
 * Client-side mirror of the Klondike rules (SolitaireEngine) — used to highlight legal destinations, to reject
 * illegal drops before they reach the server and to apply legal moves optimistically. The server stays
 * authoritative (it validates every action and flips face-down cards).
 *
 * Card = { r: 1..13, s: 'S'|'H'|'D'|'C' }. Selection / pile = { pile: 'WASTE'|'TABLEAU'|'FOUNDATION', index, cardIndex }
 * where cardIndex counts from the bottom of the whole column (face-down cards included).
 */
export const SUITS = ['S', 'H', 'D', 'C'];
export const SUIT_GLYPH = { S: '♠', H: '♥', D: '♦', C: '♣' };
const RANKS = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };

export const isRed = (c) => c?.s === 'H' || c?.s === 'D';
export const rankLabel = (r) => RANKS[r] || String(r);
export const cardKey = (c) => (c ? `${c.r}${c.s}` : '');

export const DEFAULT_THRESHOLDS = [10, 26, 52];

export function thresholdsOf(state) {
  const t = state?.thresholds;
  return Array.isArray(t) && t.length === 3 ? t : DEFAULT_THRESHOLDS;
}

export function starsFor(count, thresholds) {
  let n = 0;
  thresholds.forEach((t) => { if (count >= t) n += 1; });
  return n;
}

/** Cards of a selection (bottom → top), or [] when it is not a valid face-up source. */
export function sourceCards(state, sel) {
  if (!state || !sel) return [];
  if (sel.pile === 'WASTE') {
    const w = state.waste || [];
    return w.length ? [w[w.length - 1]] : [];
  }
  if (sel.pile === 'FOUNDATION') {
    const top = state.foundations?.[sel.index];
    return top ? [top] : [];
  }
  const col = state.tableau?.[sel.index];
  if (!col) return [];
  const i = sel.cardIndex - col.down;
  if (i < 0 || i >= col.up.length) return [];
  return col.up.slice(i);
}

export function fitsTableau(card, col) {
  if (!card || !col) return false;
  if (!col.up.length) return col.down === 0 && card.r === 13;
  const top = col.up[col.up.length - 1];
  return top.r === card.r + 1 && isRed(top) !== isRed(card);
}

export function fitsFoundation(card, foundations) {
  if (!card) return false;
  const idx = SUITS.indexOf(card.s);
  const top = foundations?.[idx];
  return (top ? top.r : 0) === card.r - 1;
}

/** Legal destinations of a selection: [{pile:'FOUNDATION', index:suit} | {pile:'TABLEAU', index}]. */
export function legalDestinations(state, sel) {
  const cards = sourceCards(state, sel);
  if (!cards.length) return [];
  const first = cards[0];
  const out = [];
  if (cards.length === 1 && sel.pile !== 'FOUNDATION' && fitsFoundation(first, state.foundations)) {
    out.push({ pile: 'FOUNDATION', index: SUITS.indexOf(first.s) });
  }
  (state.tableau || []).forEach((col, j) => {
    if (sel.pile === 'TABLEAU' && sel.index === j) return;
    if (!fitsTableau(first, col)) return;
    // a column that already starts with a King is never moved to another empty column
    if (sel.pile === 'TABLEAU' && !col.up.length && sel.cardIndex === 0) return;
    out.push({ pile: 'TABLEAU', index: j });
  });
  return out;
}

export const sameDest = (a, b) => !!a && !!b && a.pile === b.pile && a.index === b.index;

/** Request piles: send only what the server reads. */
export function toFrom(sel) {
  if (sel.pile === 'WASTE') return { pile: 'WASTE' };
  if (sel.pile === 'FOUNDATION') return { pile: 'FOUNDATION', index: sel.index };
  return { pile: 'TABLEAU', index: sel.index, cardIndex: sel.cardIndex };
}

/** Destination of a drop: any foundation slot means "the foundation of the card's suit". */
export function normalizeDest(state, sel, dest) {
  if (!dest) return null;
  if (dest.pile !== 'FOUNDATION') return { pile: dest.pile, index: dest.index };
  const first = sourceCards(state, sel)[0];
  return first ? { pile: 'FOUNDATION', index: SUITS.indexOf(first.s) } : null;
}

export function isLegalMove(state, sel, dest) {
  const d = normalizeDest(state, sel, dest);
  return !!d && legalDestinations(state, sel).some((x) => sameDest(x, d));
}

/**
 * Optimistic result of a legal MOVE (null when illegal). Cards move, the move counter drops by one; a face-down card
 * uncovered by the move stays face-down here — the server response flips it.
 */
export function applyMove(state, sel, dest) {
  if (!isLegalMove(state, sel, dest)) return null;
  const d = normalizeDest(state, sel, dest);
  const cards = sourceCards(state, sel);
  const next = {
    ...state,
    tableau: state.tableau.map((col) => ({ ...col, up: [...col.up] })),
    foundations: [...(state.foundations || [null, null, null, null])],
    waste: [...(state.waste || [])],
    hint: null,
  };
  let count = state.foundationCount || 0;
  if (sel.pile === 'WASTE') {
    next.waste.pop();
    next.wasteCount = Math.max(0, (state.wasteCount || 0) - 1);
  } else if (sel.pile === 'FOUNDATION') {
    const top = cards[0];
    next.foundations[sel.index] = top.r > 1 ? { r: top.r - 1, s: top.s } : null;
    count -= 1;
  } else {
    const col = next.tableau[sel.index];
    col.up.splice(sel.cardIndex - col.down);
  }
  if (d.pile === 'FOUNDATION') {
    next.foundations[d.index] = cards[0];
    count += 1;
  } else {
    next.tableau[d.index].up.push(...cards);
  }
  next.foundationCount = count;
  if (typeof state.movesLeft === 'number') next.movesLeft = Math.max(0, state.movesLeft - 1);
  return next;
}
